import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { db } from '../firebase';
import { doc, updateDoc, setDoc, onSnapshot, collection } from 'firebase/firestore';
import { useAuth } from '../AuthProvider';
import { useUI } from '../UIContext';
import PageWrapperAuto from '../components/PageWrapperAuto';
import { loadSetHybrid } from '../utils/loadSetHybrid';
import { flushPendingWrites } from '../utils/flushPendingWrites';
import { useUserPrefs } from '../UserPrefsProvider';
import { normalizeKey, normalizeType, keyRoot, normalizeGenre, getNormalizedGenreOptions } from '../utils/normalize';

export default function FillProgramPage() {
  const { inputStyle } = useUI();
  const { user } = useAuth();
  const { prefs } = useUserPrefs();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: programId } = useParams();

  // Edit mode: pre-populate from an existing program-created set
  const editState = location.state || {};
  const editingSetId = editState.editingSetId || null;
  const editingSetTitle = editState.editingSetTitle || null;
  const existingTunes = editState.existingTunes || null;

  const [programData, setProgramData] = useState(null);
  const [slots, setSlots] = useState([]); // { name, order, assignedTune: null | tuneObj, textNote: '', isExtra: bool }
  const [setTitle, setSetTitle] = useState('');
  const [allTunes, setAllTunes] = useState([]);
  const [expandedSlot, setExpandedSlot] = useState(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterKey, setFilterKey] = useState('');
  const [filterGenre, setFilterGenre] = useState('');
  const [showTunePicker, setShowTunePicker] = useState(true);

  const currentMode = prefs?.appMode || 'tunes';

  // Load program data
  useEffect(() => {
    if (!user || !programId) return;

    const load = async () => {
      try {
        if (navigator.onLine) await flushPendingWrites(user);
        const data = await loadSetHybrid(user, programId);
        if (data) {
          setProgramData(data);
          setSlots((data.slots || []).map(s => ({ ...s, assignedTune: null, textNote: '', isExtra: false })));
          if (editingSetTitle) {
            setSetTitle(editingSetTitle);
          } else {
            const today = new Date().toLocaleDateString(undefined, { dateStyle: 'medium' });
            setSetTitle(`${data.title || 'Program'} - ${today}`);
          }
        }
      } catch (err) {
        console.error('Error loading program:', err);
      }
    };
    load();
  }, [user, programId, editingSetTitle]);

  // Pre-populate slots from existing tunes when editing a program-created set
  useEffect(() => {
    if (!existingTunes || !allTunes.length || !slots.length) return;
    // Only run once: check if any slots are already assigned
    if (slots.some(s => s.assignedTune)) return;

    setSlots(prev => {
      // First, map template slots
      const mapped = prev.map(slot => {
        const match = existingTunes.find(t => t.slotName === slot.name && !t.isExtra);
        if (!match) return slot;
        if (match.type === 'note') {
          return { ...slot, textNote: match.title || '', assignedTune: null };
        }
        const fullTune = allTunes.find(t => t.id === match.id);
        return { ...slot, assignedTune: fullTune || null, textNote: match.textNote || '' };
      });
      // Then, restore extra items
      const extras = existingTunes.filter(t => t.isExtra);
      if (extras.length > 0) {
        const result = [...mapped];
        extras.forEach(extra => {
          const insertIdx = typeof extra.afterIndex === 'number' ? extra.afterIndex + 1 : result.length;
          const extraSlot = {
            name: extra.title || 'Extra',
            order: insertIdx,
            isExtra: true,
            textNote: extra.type === 'note' ? (extra.title || '') : '',
            assignedTune: extra.type === 'note' ? null : allTunes.find(t => t.id === extra.id) || null,
          };
          result.splice(Math.min(insertIdx, result.length), 0, extraSlot);
        });
        return result;
      }
      return mapped;
    });
  }, [existingTunes, allTunes, slots]);

  // Load all user tunes
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(
      collection(db, 'users', user.uid, 'tunes'),
      snapshot => {
        setAllTunes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }
    );
    return unsub;
  }, [user]);

  // Filter tunes for search
  const keyGroups = {};
  allTunes.forEach(t => {
    const nk = normalizeKey(t.key);
    if (!nk) return;
    const root = keyRoot(nk);
    if (!keyGroups[root]) keyGroups[root] = new Set();
    keyGroups[root].add(nk);
  });
  const genreOptions = getNormalizedGenreOptions(allTunes.map((t) => t.genre));

  const getFilteredTunes = () => {
    return allTunes.filter(item => {
      const query = search.toLowerCase();
      const nk = normalizeKey(item.key || '');
      const matchTitle = !query || item.title?.toLowerCase().includes(query);
      const matchType = !filterType || normalizeType(item.type) === normalizeType(filterType);
      const matchKey = !filterKey
        || nk === filterKey
        || (filterKey.startsWith('root:') && keyRoot(nk) === filterKey.replace('root:', ''));
      const matchGenre = !filterGenre || normalizeGenre(item.genre) === filterGenre;
      return matchTitle && matchType && matchKey && matchGenre;
    });
  };

  const filledCount = slots.filter(s => s.assignedTune || s.textNote?.trim()).length;

  const handleSave = async () => {
    if (!user) return;

    const filledTunes = slots
      .filter(s => s.assignedTune || s.textNote?.trim())
      .map((s, idx) => {
        const entry = s.assignedTune
          ? {
              id: s.assignedTune.id,
              title: s.assignedTune.title || '',
              slotName: s.name || '',
              type: 'tune',
              key: s.assignedTune.key || '',
              tuneType: s.assignedTune.type || '',
              genre: s.assignedTune.genre || '',
              textNote: s.textNote?.trim() || '',
              isExtra: s.isExtra || false,
            }
          : {
              id: `note_${Date.now()}_${idx}`,
              title: s.textNote.trim(),
              slotName: s.name || '',
              type: 'note',
              key: '',
              tuneType: '',
              genre: '',
              isExtra: s.isExtra || false,
            };
        if (s.isExtra) entry.afterIndex = idx - 1;
        return entry;
      });

    try {
      if (editingSetId) {
        // Update existing set
        const ref = doc(db, 'users', user.uid, 'sets', editingSetId);
        await updateDoc(ref, {
          title: setTitle.trim() || programData?.title || 'Untitled Set',
          tunes: filledTunes,
          updatedAt: new Date().toISOString()
        });
        navigate(`/sets/${editingSetId}`);
      } else {
        // Create new set
        const newSet = {
          title: setTitle.trim() || programData?.title || 'Untitled Set',
          notes: '',
          tunes: filledTunes,
          genre: programData?.genre || '',
          rating: '',
          customTags: [],
          mode: currentMode === 'all' ? 'tunes' : currentMode,
          fromProgram: programId,
          fromProgramTitle: programData?.title || '',
          createdAt: new Date().toISOString()
        };
        const newSetId = crypto.randomUUID();
        await setDoc(doc(db, 'users', user.uid, 'sets', newSetId), { id: newSetId, ...newSet });
        navigate(`/sets/${newSetId}`);
      }
    } catch (err) {
      console.error('Error saving set:', err);
      alert('Failed to save set');
    }
  };

  if (!programData) {
    return <PageWrapperAuto><p>Loading program...</p></PageWrapperAuto>;
  }

  const filtered = getFilteredTunes();

  return (
    <PageWrapperAuto>
      <h2>{editingSetId ? 'Edit Program Set' : 'Use Program'}</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginTop: 0 }}>
        Pick a tune or add a text note for each slot. Use "+ insert" to add extra items.
      </p>

      <div style={{ maxWidth: '600px', margin: '0 auto 1rem' }}>
        <input
          style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', fontSize: '1.1rem' }}
          value={setTitle}
          onChange={e => setSetTitle(e.target.value)}
          placeholder="Set title"
        />
      </div>

      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        {slots.map((slot, i) => (
          <React.Fragment key={i}>
          <div
            style={{
              border: '1px solid var(--border-color, #ccc)',
              borderRadius: '8px',
              marginBottom: '4px',
              overflow: 'hidden',
              backgroundColor: 'var(--input-bg)',
            }}
          >
            {/* Slot header */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 12px',
                cursor: 'pointer',
              }}
              onClick={() => {
                setExpandedSlot(expandedSlot === i ? null : i);
                setSearch('');
                setFilterType('');
                setFilterKey('');
                setFilterGenre(programData?.genre ? programData.genre.trim().toLowerCase() : '');
                setShowTunePicker(true);
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong>{i + 1}. {slot.isExtra ? <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>(added)</span> : slot.name}</strong>
                {slot.assignedTune && (
                  <span style={{ marginLeft: '8px', color: 'var(--text-secondary)' }}>
                    — {slot.assignedTune.title}
                    {slot.assignedTune.key ? ` (${slot.assignedTune.key})` : ''}
                  </span>
                )}
                {!slot.assignedTune && slot.textNote?.trim() && (
                  <span style={{ marginLeft: '8px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                    — {slot.textNote}
                  </span>
                )}
                {!slot.assignedTune && !slot.textNote?.trim() && (
                  <span style={{ marginLeft: '8px', color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '0.85rem' }}>
                    (empty)
                  </span>
                )}
                {slot.assignedTune && slot.textNote?.trim() && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '2px', paddingLeft: '1.5em' }}>
                    {slot.textNote}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                {(slot.assignedTune || slot.textNote?.trim() || slot.isExtra) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (slot.isExtra) {
                        setSlots(prev => prev.filter((_, idx) => idx !== i));
                      } else {
                        setSlots(prev => prev.map((s, idx) => idx === i ? { ...s, assignedTune: null, textNote: '' } : s));
                      }
                    }}
                    style={{ ...inputStyle, padding: '2px 8px', fontSize: '0.8rem' }}
                  >
                    &times;
                  </button>
                )}
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  {expandedSlot === i ? '▲' : '▼'}
                </span>
              </div>
            </div>

            {/* Expanded panel */}
            {expandedSlot === i && (
              <div style={{ padding: '0 12px 12px', borderTop: '1px solid var(--border-color, #ccc)' }}>
                {/* Text note input — always visible */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px', marginBottom: '8px' }}>
                  <input
                    value={slot.textNote || ''}
                    onChange={e => {
                      const val = e.target.value;
                      setSlots(prev => prev.map((s, idx) => idx === i ? { ...s, textNote: val } : s));
                    }}
                    placeholder="Text note (tune name, instruction, etc.)"
                    style={{ ...inputStyle, flex: 1, fontSize: '0.85rem' }}
                    onKeyDown={e => { if (e.key === 'Enter' && !showTunePicker) setExpandedSlot(null); }}
                  />
                  {!showTunePicker && (
                    <button
                      style={{ ...inputStyle, padding: '4px 10px', fontSize: '0.8rem' }}
                      onClick={() => setShowTunePicker(true)}
                      title="Show tune picker"
                    >
                      + Tune
                    </button>
                  )}
                </div>

                {/* Tune picker */}
                {showTunePicker && (
                  <>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search by title"
                        style={{ ...inputStyle, flex: 1, minWidth: '120px' }}
                        autoFocus
                      />
                    </div>

                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                      <select value={filterGenre} onChange={e => setFilterGenre(e.target.value)} style={inputStyle}>
                        <option value="">All genres</option>
                        {genreOptions.map((g, idx) => (
                          <option key={idx} value={g}>{g}</option>
                        ))}
                      </select>

                      <select value={filterType} onChange={e => setFilterType(e.target.value)} style={inputStyle}>
                        <option value="">All types</option>
                        {[...new Set(allTunes.map(t => normalizeType(t.type)).filter(Boolean))].sort().map(type => (
                          <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                        ))}
                      </select>

                      <select value={filterKey} onChange={e => setFilterKey(e.target.value)} style={inputStyle}>
                        <option value="">All keys</option>
                        {Object.keys(keyGroups).sort().map(root => (
                          <optgroup key={root} label={root}>
                            <option value={`root:${root}`}>{root} (All Modes)</option>
                            {[...keyGroups[root]].sort((a, b) => {
                              const rank = k => {
                                if (k.endsWith(' mix')) return 3;
                                if (k.endsWith(' dorian')) return 2;
                                if (k.endsWith('m')) return 1;
                                return 0;
                              };
                              return rank(a) - rank(b) || a.localeCompare(b);
                            }).map(k => {
                              const label =
                                k.endsWith('m') ? root + ' minor' :
                                k.endsWith(' dorian') ? root + ' dorian' :
                                k.endsWith(' mix') ? root + ' mixolydian' :
                                root + ' major';
                              return <option key={k} value={k}>{label}</option>;
                            })}
                          </optgroup>
                        ))}
                      </select>
                    </div>

                    <div style={{
                      maxHeight: '180px',
                      overflowY: 'auto',
                      borderRadius: '6px',
                      backgroundColor: 'var(--input-bg)',
                      color: 'var(--text-color, inherit)',
                      padding: '4px',
                    }}>
                      {filtered.length === 0 && (
                        <div style={{ padding: '8px', color: 'var(--text-muted)', fontSize: '0.85rem', textAlign: 'center' }}>
                          No tunes found
                        </div>
                      )}
                      {filtered.map(tune => {
                        const isSelected = slot.assignedTune?.id === tune.id;
                        return (
                          <div
                            key={tune.id}
                            style={{
                              padding: '6px 8px',
                              cursor: 'pointer',
                              backgroundColor: isSelected ? 'var(--accent-light, #e3f2fd)' : 'transparent',
                              color: 'var(--text-color, inherit)',
                              fontWeight: isSelected ? 'bold' : 'normal',
                              borderRadius: '4px',
                            }}
                            onClick={() => {
                              setSlots(prev => prev.map((s, idx) => idx === i ? { ...s, assignedTune: tune } : s));
                              setExpandedSlot(null);
                            }}
                          >
                            {tune.title}
                            {tune.type && <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}> ({[tune.type, tune.key].filter(Boolean).join(', ')})</span>}
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}

          </div>

            {/* Add item button between slots */}
            {expandedSlot === null && (
              <div style={{ textAlign: 'center', marginBottom: '4px' }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const newSlot = {
                      name: '',
                      order: i + 0.5,
                      assignedTune: null,
                      textNote: '',
                      isExtra: true,
                    };
                    setSlots(prev => {
                      const updated = [...prev];
                      updated.splice(i + 1, 0, newSlot);
                      return updated;
                    });
                    setExpandedSlot(i + 1);
                    setShowTunePicker(true);
                    setSearch('');
                    setFilterType('');
                    setFilterKey('');
                    setFilterGenre(programData?.genre ? programData.genre.trim().toLowerCase() : '');
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-muted)',
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    padding: '2px 8px',
                  }}
                  title="Add an item here"
                >
                  + insert
                </button>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      <p style={{ textAlign: 'center', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
        {filledCount} of {slots.length} slots filled
      </p>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', margin: '16px 0' }}>
        <button style={inputStyle} onClick={handleSave}>
          {editingSetId ? 'Update Set' : 'Create Set'}
        </button>
        <button
          style={{ ...inputStyle, backgroundColor: 'var(--button-bg, #ddd)', color: 'var(--text-primary, #000)' }}
          onClick={() => navigate(editingSetId ? `/sets/${editingSetId}` : `/sets/${programId}`)}
        >
          Cancel
        </button>
      </div>
    </PageWrapperAuto>
  );
}
